import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import JoinViewForm from "../JoinViewForm";
import LightContentContainer from "../../../components/layout/LightContentContainer";
import { normalizeRecoveryCode } from "../../play/topik/recoveryCode";

const minimumGameCodeLength = 4;
const recoveryCodeLength = 8; // 8 chars, XXXX-XXXX (§7)

// A too-short code gets its own message: the red border alone doesn't tell it (WCAG 1.4.1).
const getGameCodeError = ({ t, joinError, gameCodeValid = true }) => {
  if (!gameCodeValid) {
    return t("join-view.form.gamecode-too-short", { count: minimumGameCodeLength });
  }
  if (joinError === "invalid_game_code") {
    return t("join-view.form.gamecode-error");
  }
  if (joinError === "rate_limited") {
    return t("join-view.form.rate-limit-error");
  }
  if (joinError === "network_error") {
    return t("join-view.form.generic-error");
  }

  return "";
};

const getRejoinError = ({ t, rejoinError, recoveryValid = true }) => {
  if (!recoveryValid) {
    return t("join-view.resume.code-length");
  }
  if (rejoinError === "invalid_game_code") {
    return t("join-view.resume.gamecode-error");
  }
  if (rejoinError === "invalid_recovery_code") {
    return t("join-view.resume.code-error");
  }
  if (rejoinError === "rate_limited") {
    return t("join-view.form.rate-limit-error");
  }
  if (rejoinError === "network_error") {
    return t("join-view.resume.generic-error");
  }

  return "";
};

const JoinNewGameContent = ({
  joinError = null,
  isJoining = false,
  onJoinGame,
  gameCode = "",
  gameCodeTouched = false,
  onGameCodeTouched,
  onGameCodeChanged,
  onClearErrors,
  rejoinError = null,
  isRejoining = false,
  onRejoinGame,
  onClearRejoinErrors,
}) => {
  const { t } = useTranslation("views");
  const [resuming, setResuming] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState("");
  const [recoveryTouched, setRecoveryTouched] = useState(false);

  const hasJoinError = joinError !== null;
  const gameCodeValid = gameCode.length >= minimumGameCodeLength;
  const canJoinGame = gameCode.length > 0 && gameCodeValid;

  const normalizedRecovery = normalizeRecoveryCode(recoveryCode);
  const recoveryValid = normalizedRecovery.length === recoveryCodeLength;
  const canRejoin = gameCodeValid && recoveryValid;
  // In the resume form a server error is shown under the resume code; the game code field only
  // gets a blank label then, so both fields turn invalid together.
  let resumeGameCodeError = getRejoinError({ t, rejoinError }).length > 0 ? " " : "";
  if (!gameCodeValid) {
    resumeGameCodeError = getGameCodeError({ t, gameCodeValid });
  }

  return (
    <motion.div
      className="join-new-game-content"
      initial={{ opacity: 0, x: -50 }}
      animate={{
        opacity: 1,
        x: 0,
        transition: { type: "spring", damping: 30, mass: 1 },
      }}>
      <LightContentContainer>
        <h1>{t("join-view.title")}</h1>

        {resuming ? (
          <JoinViewForm
            name={t("join-view.resume.aria.form-label")}
            inputs={[
              {
                id: "resume-gamecode-input",
                label: t("join-view.form.gamecode-label"),
                placeholder: t("join-view.form.gamecode-placeholder"),
                errorLabel: resumeGameCodeError,
                isValid: gameCodeValid,
                isTouched: gameCodeTouched,
                value: gameCode,
                maxLength: 20,
                onChange: (_, value) => {
                  onGameCodeChanged(value.toUpperCase());
                  onClearRejoinErrors();
                },
                onBlur: () => onGameCodeTouched(true),
              },
              {
                id: "resume-code-input",
                label: t("join-view.resume.code-label"),
                labelHelp: t("join-view.resume.code-help"),
                placeholder: t("join-view.resume.code-placeholder"),
                errorLabel: getRejoinError({ t, rejoinError, recoveryValid }),
                isValid: recoveryValid && rejoinError === null,
                isTouched: recoveryTouched,
                value: recoveryCode,
                maxLength: 9,
                onChange: (_, value) => {
                  setRecoveryCode(value);
                  onClearRejoinErrors();
                },
                onBlur: () => setRecoveryTouched(true),
              },
            ]}
            submitButton={{
              content: isRejoining ? t("join-view.resume.submitting") : t("join-view.resume.submit"),
              disabled: !canRejoin || isRejoining,
              onClick: (e) => {
                e.preventDefault();
                onRejoinGame(gameCode, normalizedRecovery);
              },
            }}
          />
        ) : (
          <JoinViewForm
            name={t("join-view.aria.join-form-label")}
            inputs={[
              {
                id: "gamecode-input",
                label: t("join-view.form.gamecode-label"),
                placeholder: t("join-view.form.gamecode-placeholder"),
                errorLabel: getGameCodeError({ t, joinError, gameCodeValid }),
                isValid: gameCodeValid && !hasJoinError,
                isTouched: gameCodeTouched,
                value: gameCode,
                maxLength: 20,
                onChange: (_, value) => {
                  onGameCodeChanged(value.toUpperCase());
                  onClearErrors();
                },
                onBlur: () => {
                  onGameCodeTouched(true);
                },
              },
            ]}
            submitButton={{
              content: isJoining ? t("join-view.form.submitting") : t("join-view.form.submit"),
              disabled: !canJoinGame || isJoining,
              onClick: (e) => {
                e.preventDefault();
                onJoinGame(gameCode);
              },
            }}
          />
        )}

        <button
          type="button"
          className="join-view__resume-toggle"
          onClick={() => {
            setResuming((current) => !current);
            onClearErrors();
            onClearRejoinErrors();
          }}
        >
          {resuming ? t("join-view.resume.back-to-join") : t("join-view.resume.prompt")}
        </button>
      </LightContentContainer>
    </motion.div>
  );
};

JoinNewGameContent.propTypes = {
  joinError: PropTypes.string,
  isJoining: PropTypes.bool,
  onJoinGame: PropTypes.func.isRequired,
  onClearErrors: PropTypes.func.isRequired,
  gameCode: PropTypes.string,
  gameCodeTouched: PropTypes.bool,
  onGameCodeTouched: PropTypes.func.isRequired,
  onGameCodeChanged: PropTypes.func.isRequired,
  rejoinError: PropTypes.string,
  isRejoining: PropTypes.bool,
  onRejoinGame: PropTypes.func.isRequired,
  onClearRejoinErrors: PropTypes.func.isRequired,
};

export default JoinNewGameContent;
